import axios from "axios"
import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { extendPermit } from "modules/permit/api/permitService"
import { validatePermitExtensionRange } from "modules/permit/utils/permitHelpers"
import { parseTimeParts } from "./usePermitDateTime"
import { toast } from "sonner"

const TIME_MINUTE_FALLBACK = "00"

const updateDialogTimeValue = (existingValue: string, updates: Partial<{ hour: string; minute: string }>) => {
  const currentParts = parseTimeParts(existingValue)
  const nextHour = updates.hour ?? currentParts.hour
  const nextMinute = updates.minute ?? currentParts.minute

  if (!nextHour) {
    return ""
  }

  return `${nextHour}:${nextMinute || TIME_MINUTE_FALLBACK}`
}

export type ExtendPermitFieldErrors = {
  endDate: string
  endTime: string
  reason: string
  form: string
}

const EMPTY_FIELD_ERRORS: ExtendPermitFieldErrors = {
  endDate: "",
  endTime: "",
  reason: "",
  form: "",
}

type UseExtendPermitFlowParams = {
  tenantId: string
  editingPermitId?: string | null
  submitterUserId?: string | null
  permitData: {
    permitNo?: string | null
    startDate?: string | null
    startTime?: string | null
    endDate?: string | null
    endTime?: string | null
  }
  navigationState: unknown
}

export const useExtendPermitFlow = ({
  tenantId,
  editingPermitId,
  submitterUserId,
  permitData,
  navigationState,
}: UseExtendPermitFlowParams) => {
  const navigate = useNavigate()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [endDate, setEndDate] = useState("")
  const [endTime, setEndTime] = useState("")
  const [reason, setReason] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<ExtendPermitFieldErrors>(EMPTY_FIELD_ERRORS)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false)
  const [resultStatus, setResultStatus] = useState<"success" | "error">("success")
  const [resultMessage, setResultMessage] = useState("")

  const endTimeParts = useMemo(() => parseTimeParts(endTime), [endTime])

  const clearErrors = useCallback(() => {
    setError(null)
    setFieldErrors(EMPTY_FIELD_ERRORS)
  }, [])

  const openDialog = useCallback(() => {
    setEndDate(permitData.endDate || "")
    setEndTime(permitData.endTime || "")
    setReason("")
    clearErrors()
    setIsDialogOpen(true)
  }, [clearErrors, permitData.endDate, permitData.endTime])

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open === isDialogOpen) {
        return
      }
      setIsDialogOpen(open)
      if (!open) {
        clearErrors()
      }
    },
    [clearErrors, isDialogOpen],
  )

  const handleResultDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open === isResultDialogOpen) {
        return
      }
      setIsResultDialogOpen(open)
      if (!open && isResultDialogOpen && resultStatus === "success") {
        navigate("/permit-index", { state: navigationState })
      }
    },
    [isResultDialogOpen, navigate, navigationState, resultStatus],
  )

  const handleEndHourChange = useCallback((value: string) => {
    setEndTime((prev) => updateDialogTimeValue(prev, { hour: value }))
  }, [])

  const handleEndMinuteChange = useCallback((value: string) => {
    setEndTime((prev) => updateDialogTimeValue(prev, { minute: value }))
  }, [])

  const validateForm = useCallback(() => {
    const nextErrors: ExtendPermitFieldErrors = { ...EMPTY_FIELD_ERRORS }
    const currentEndDate = permitData.endDate?.trim() ?? ""
    const currentEndTime = permitData.endTime?.trim() ?? ""

    if (!editingPermitId) {
      nextErrors.form =
        "\u0e44\u0e21\u0e48\u0e1e\u0e1a Permit \u0e17\u0e35\u0e48\u0e15\u0e49\u0e2d\u0e07\u0e01\u0e32\u0e23\u0e15\u0e48\u0e2d\u0e40\u0e27\u0e25\u0e32"
    }

    if (!submitterUserId) {
      nextErrors.form =
        "\u0e44\u0e21\u0e48\u0e1e\u0e1a\u0e02\u0e49\u0e2d\u0e21\u0e39\u0e25\u0e1c\u0e39\u0e49\u0e43\u0e0a\u0e49\u0e07\u0e32\u0e19 \u0e01\u0e23\u0e38\u0e13\u0e32\u0e40\u0e02\u0e49\u0e32\u0e23\u0e30\u0e1a\u0e1a\u0e43\u0e2b\u0e21\u0e48"
    }

    if (!currentEndDate || !currentEndTime) {
      nextErrors.form =
        "\u0e44\u0e21\u0e48\u0e1e\u0e1a\u0e40\u0e27\u0e25\u0e32\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14\u0e1b\u0e31\u0e08\u0e08\u0e38\u0e1a\u0e31\u0e19\u0e02\u0e2d\u0e07 Permit"
    }

    if (!endDate) {
      nextErrors.endDate =
        "\u0e01\u0e23\u0e38\u0e13\u0e32\u0e40\u0e25\u0e37\u0e2d\u0e01\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14\u0e43\u0e2b\u0e21\u0e48"
    }

    if (!endTime) {
      nextErrors.endTime =
        "\u0e01\u0e23\u0e38\u0e13\u0e32\u0e23\u0e30\u0e1a\u0e38\u0e40\u0e27\u0e25\u0e32\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14\u0e43\u0e2b\u0e21\u0e48"
    }

    if (!reason.trim()) {
      nextErrors.reason =
        "\u0e01\u0e23\u0e38\u0e13\u0e32\u0e23\u0e30\u0e1a\u0e38\u0e40\u0e2b\u0e15\u0e38\u0e1c\u0e25\u0e43\u0e19\u0e01\u0e32\u0e23\u0e15\u0e48\u0e2d Permit"
    }

    if (!nextErrors.endDate && !nextErrors.endTime && !nextErrors.form) {
      const currentStartDate = permitData.startDate?.trim() ?? ""
      const currentStartTime = permitData.startTime?.trim() ?? ""
      const extensionValidation = validatePermitExtensionRange(
        currentEndDate,
        currentEndTime,
        endDate,
        endTime,
        currentStartDate,
        currentStartTime,
      )
      if (!extensionValidation.isValid) {
        const extensionError = extensionValidation.error ?? ""
        if (
          extensionError === "\u0e40\u0e27\u0e25\u0e32\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14\u0e43\u0e2b\u0e21\u0e48\u0e15\u0e49\u0e2d\u0e07\u0e21\u0e32\u0e01\u0e01\u0e27\u0e48\u0e32\u0e40\u0e27\u0e25\u0e32\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14\u0e40\u0e14\u0e34\u0e21" ||
          extensionError === "\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e15\u0e48\u0e2d Permit \u0e44\u0e14\u0e49\u0e44\u0e21\u0e48\u0e40\u0e01\u0e34\u0e19 24 \u0e0a\u0e31\u0e48\u0e27\u0e42\u0e21\u0e07\u0e08\u0e32\u0e01\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e40\u0e23\u0e34\u0e48\u0e21\u0e15\u0e49\u0e19"
        ) {
          nextErrors.endTime = extensionError
        } else {
          nextErrors.form = extensionError
        }
      }
    }

    setFieldErrors(nextErrors)
    setError(nextErrors.form || null)

    const hasError = Object.values(nextErrors).some((value) => value.trim().length > 0)
    if (hasError || !editingPermitId || !submitterUserId) {
      return null
    }

    return {
      newExtendedEnd: `${endDate}T${endTime}`,
    }
  }, [editingPermitId, endDate, endTime, permitData.endDate, permitData.endTime, permitData.startDate, permitData.startTime, reason, submitterUserId])

  const handleSubmit = useCallback(async () => {
    const validated = validateForm()
    if (!validated || !editingPermitId || !submitterUserId) {
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await extendPermit(editingPermitId, {
        tenantId,
        permitId: editingPermitId,
        requestedByUserId: submitterUserId,
        newExtendedEnd: validated.newExtendedEnd,
        reason: reason.trim(),
      })

      const workflowWarnings =
        response.policyWarnings?.map((warning) => warning.message?.trim()).filter(Boolean).join(" | ") ?? ""
      const emailWarning = response.emailErrorMessage?.trim() ?? ""

      if (emailWarning) {
        toast.error(
          `\u0e2a\u0e48\u0e07\u0e04\u0e33\u0e02\u0e2d\u0e15\u0e48\u0e2d Permit \u0e41\u0e25\u0e49\u0e27 \u0e41\u0e15\u0e48\u0e2a\u0e48\u0e07\u0e2d\u0e35\u0e40\u0e21\u0e25\u0e44\u0e21\u0e48\u0e2a\u0e33\u0e40\u0e23\u0e47\u0e08: ${emailWarning}`,
        )
      }

      setIsDialogOpen(false)
      setResultStatus("success")
      setResultMessage(
        [
          "\u0e2a\u0e48\u0e07\u0e04\u0e33\u0e02\u0e2d\u0e15\u0e48\u0e2d Permit \u0e2a\u0e33\u0e40\u0e23\u0e47\u0e08 \u0e41\u0e25\u0e30\u0e40\u0e23\u0e34\u0e48\u0e21 Workflow \u0e43\u0e2b\u0e21\u0e48\u0e41\u0e25\u0e49\u0e27",
          workflowWarnings ? `\u0e04\u0e33\u0e40\u0e15\u0e37\u0e2d\u0e19: ${workflowWarnings}` : "",
          emailWarning ? `\u0e2b\u0e21\u0e32\u0e22\u0e40\u0e2b\u0e15\u0e38\u0e2d\u0e35\u0e40\u0e21\u0e25: ${emailWarning}` : "",
        ]
          .filter(Boolean)
          .join("\n\n"),
      )
      setIsResultDialogOpen(true)
    } catch (caughtError) {
      const message = axios.isAxiosError(caughtError)
        ? ((caughtError.response?.data as { message?: string } | undefined)?.message ??
            caughtError.response?.statusText ??
            caughtError.message ??
            "\u0e44\u0e21\u0e48\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e15\u0e48\u0e2d Permit \u0e44\u0e14\u0e49")
        : "\u0e44\u0e21\u0e48\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e15\u0e48\u0e2d Permit \u0e44\u0e14\u0e49"

      setError(message)
      setFieldErrors((prev) => ({ ...prev, form: message }))
      setResultStatus("error")
      setResultMessage(message)
      setIsResultDialogOpen(true)
    } finally {
      setIsSubmitting(false)
    }
  }, [editingPermitId, reason, submitterUserId, tenantId, validateForm])

  return {
    dialog: {
      open: isDialogOpen,
      permitNumber: permitData.permitNo,
      currentStartDate: permitData.startDate ?? "",
      currentStartTime: permitData.startTime ?? "",
      currentEndDate: permitData.endDate ?? "",
      currentEndTime: permitData.endTime ?? "",
      isSubmitting,
      endDate,
      endHour: endTimeParts.hour,
      endMinute: endTimeParts.minute,
      reason,
      error,
      fieldErrors,
      onOpenChange: handleDialogOpenChange,
      onEndDateChange: setEndDate,
      onEndHourChange: handleEndHourChange,
      onEndMinuteChange: handleEndMinuteChange,
      onReasonChange: setReason,
      onSubmit: handleSubmit,
    },
    resultDialog: {
      open: isResultDialogOpen,
      status: resultStatus,
      message: resultMessage,
      onOpenChange: handleResultDialogOpenChange,
    },
    actions: {
      openDialog,
    },
    state: {
      isSubmitting,
    },
  }
}
